import LogoSvg from '@/assets/wntd-wordmark-orange.svg';

interface PriceGuessRound {
  itemName: string;
  itemImage?: string;
  guessedPrice: number;
  actualPrice: number;
  currency: string;
  scoreTier: string;
}

interface PriceGuessShareCardProps {
  totalScore: number;
  avgAccuracy: number;
  perfectCount: number;
  streak: number;
  level: number;
  levelTitle?: string;
  rounds?: PriceGuessRound[];
}

const TIER_EMOJI: Record<string, string> = {
  perfect:   '🎯',
  excellent: '🔥',
  good:      '👍',
  close:     '😏',
  miss:      '😅',
};

const TIER_COLOR: Record<string, string> = {
  perfect:   '#10B981',
  excellent: '#3B82F6',
  good:      '#FF6F3B',
  close:     '#F59E0B',
  miss:      '#6B7280',
};

export function PriceGuessShareCard({
  totalScore,
  avgAccuracy,
  perfectCount,
  streak,
  level,
  levelTitle = 'Player',
  rounds = [],
}: PriceGuessShareCardProps) {
  const displayRounds = rounds.slice(0, 5);

  return (
    <div
      style={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: '#0a0a0a',
        padding: '48px 48px 40px',
      }}
    >
      {/* Score hero */}
      <div
        style={{
          flexShrink: 0,
          padding: '44px 40px 40px',
          borderRadius: 32,
          background: 'linear-gradient(135deg, #FF5C4D 0%, #FF6F3B 100%)',
          marginBottom: 20,
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Subtle inner glow */}
        <div
          style={{
            position: 'absolute',
            top: -80,
            right: -80,
            width: 300,
            height: 300,
            borderRadius: '50%',
            background: 'rgba(255,255,255,0.10)',
            pointerEvents: 'none',
          }}
        />

        <p
          style={{
            fontSize: 20,
            color: 'rgba(255,255,255,0.7)',
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: 2.5,
            marginBottom: 8,
          }}
        >
          Price Guess Daily
        </p>

        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 10, marginBottom: 12 }}>
          <span style={{ fontSize: 100, fontWeight: 900, color: 'white', lineHeight: 1 }}>
            {totalScore}
          </span>
          <span
            style={{
              fontSize: 34,
              fontWeight: 700,
              color: 'rgba(255,255,255,0.7)',
              paddingBottom: 12,
            }}
          >
            XP
          </span>
        </div>

        <p style={{ fontSize: 22, color: 'rgba(255,255,255,0.75)', fontWeight: 500 }}>
          Level {level} · {levelTitle}
        </p>
      </div>

      {/* Stats trio */}
      <div style={{ display: 'flex', gap: 16, marginBottom: 20, flexShrink: 0 }}>
        {[
          { emoji: '🎯', value: `${avgAccuracy}%`,    label: 'Avg Accuracy' },
          { emoji: '💎', value: `${perfectCount}`,    label: 'Perfect Guess' + (perfectCount !== 1 ? 'es' : '') },
          { emoji: '🔥', value: `${streak}`,          label: 'Day Streak' },
        ].map((s, i) => (
          <div
            key={i}
            style={{
              flex: 1,
              padding: '24px 16px 20px',
              borderRadius: 24,
              backgroundColor: 'rgba(255,255,255,0.05)',
              border: '1px solid rgba(255,255,255,0.08)',
              textAlign: 'center',
            }}
          >
            <span style={{ fontSize: 30, display: 'block', marginBottom: 10 }}>{s.emoji}</span>
            <p style={{ fontSize: 38, fontWeight: 800, color: 'white', lineHeight: 1, marginBottom: 6 }}>
              {s.value}
            </p>
            <p style={{ fontSize: 18, color: 'rgba(255,255,255,0.4)', fontWeight: 500 }}>{s.label}</p>
          </div>
        ))}
      </div>

      {/* Round breakdown */}
      {displayRounds.length > 0 && (
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 10 }}>
          {displayRounds.map((round, i) => {
            const tier  = TIER_EMOJI[round.scoreTier]  || '😅';
            const color = TIER_COLOR[round.scoreTier]  || '#6B7280';
            return (
              <div
                key={i}
                style={{
                  flex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 18,
                  padding: '0 22px',
                  borderRadius: 20,
                  backgroundColor: 'rgba(255,255,255,0.04)',
                  border: '1px solid rgba(255,255,255,0.06)',
                }}
              >
                {/* Product image or round number */}
                {round.itemImage ? (
                  <div
                    style={{
                      width: 52,
                      height: 52,
                      borderRadius: 14,
                      overflow: 'hidden',
                      flexShrink: 0,
                      backgroundColor: '#1a1a1a',
                    }}
                  >
                    <img
                      src={round.itemImage}
                      alt={round.itemName}
                      crossOrigin="anonymous"
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                  </div>
                ) : (
                  <div
                    style={{
                      width: 52,
                      height: 52,
                      borderRadius: 14,
                      flexShrink: 0,
                      backgroundColor: 'rgba(255,255,255,0.08)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 20,
                      fontWeight: 700,
                      color: 'rgba(255,255,255,0.4)',
                    }}
                  >
                    {i + 1}
                  </div>
                )}

                {/* Item name + prices */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p
                    style={{
                      fontSize: 22,
                      fontWeight: 600,
                      color: 'white',
                      lineHeight: 1.2,
                      overflow: 'hidden',
                      display: '-webkit-box',
                      WebkitLineClamp: 1,
                      WebkitBoxOrient: 'vertical',
                    }}
                  >
                    {round.itemName}
                  </p>
                  <p style={{ fontSize: 18, color: 'rgba(255,255,255,0.38)', marginTop: 3 }}>
                    Guessed {round.currency}{round.guessedPrice} · Actual {round.currency}{round.actualPrice}
                  </p>
                </div>

                {/* Tier emoji + color dot */}
                <div
                  style={{
                    flexShrink: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 4,
                  }}
                >
                  <span style={{ fontSize: 26 }}>{tier}</span>
                  <div
                    style={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      backgroundColor: color,
                    }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Footer */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          paddingTop: 36,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <img src={LogoSvg} alt="WNTD" style={{ height: 36 }} />
          <span style={{ fontSize: 22, color: 'rgba(255,255,255,0.4)', fontWeight: 500 }}>
            Price Guess
          </span>
        </div>
        <span style={{ fontSize: 22, color: 'rgba(255,255,255,0.3)' }}>wntd.ai</span>
      </div>
    </div>
  );
}
